import { cn } from "@/lib/utils";
import { Check, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export interface AdTemplate {
  id: string;
  name: string;
  description: string;
  preview: string;
  style: string;
  creativeType: string;
  textConfig: {
    title: string;
    subtitle: string;
    cta: string;
    price?: string;
    discount?: string;
  };
  isNew?: boolean;
}

const AD_TEMPLATES: AdTemplate[] = [
  {
    id: "promo-flash",
    name: "Promo Flash",
    description: "Créative urgente avec réduction",
    preview: "🔥",
    style: "red-black",
    creativeType: "promo",
    textConfig: {
      title: "OFFRE FLASH",
      subtitle: "Édition limitée",
      cta: "J'EN PROFITE →",
      discount: "-50%",
    },
    isNew: true,
  },
  {
    id: "elegant-showcase",
    name: "Showcase Élégant",
    description: "Présentation produit premium",
    preview: "✨",
    style: "black-gold",
    creativeType: "showcase",
    textConfig: {
      title: "Collection Premium",
      subtitle: "Qualité exceptionnelle",
      cta: "Découvrir",
    },
  },
  {
    id: "eco-friendly",
    name: "Éco-Responsable",
    description: "Style naturel et durable",
    preview: "🌿",
    style: "green-nature",
    creativeType: "showcase",
    textConfig: {
      title: "100% Naturel",
      subtitle: "Respectueux de l'environnement",
      cta: "En savoir plus",
    },
  },
  {
    id: "tech-modern",
    name: "Tech Moderne",
    description: "Style futuriste et innovant",
    preview: "🚀",
    style: "blue-tech",
    creativeType: "showcase",
    textConfig: {
      title: "Innovation",
      subtitle: "La technologie du futur",
      cta: "Essayer maintenant",
    },
    isNew: true,
  },
  {
    id: "lifestyle-cozy",
    name: "Lifestyle Cosy",
    description: "Ambiance chaleureuse",
    preview: "🏠",
    style: "warm-beige",
    creativeType: "showcase",
    textConfig: {
      title: "Votre Chez-Vous",
      subtitle: "Confort et élégance",
      cta: "Voir plus",
    },
  },
  {
    id: "neon-trendy",
    name: "Néon Trendy",
    description: "Style vibrant et jeune",
    preview: "💜",
    style: "neon-gradient",
    creativeType: "showcase",
    textConfig: {
      title: "BE BOLD",
      subtitle: "Express yourself",
      cta: "Shop Now",
    },
  },
  {
    id: "minimalist-pure",
    name: "Minimaliste Pure",
    description: "Simplicité et élégance",
    preview: "⚪",
    style: "pure-white",
    creativeType: "showcase",
    textConfig: {
      title: "Simplicité",
      subtitle: "L'essentiel seulement",
      cta: "Découvrir",
    },
  },
  {
    id: "mega-soldes",
    name: "Méga Soldes",
    description: "Grande promotion",
    preview: "💰",
    style: "red-black",
    creativeType: "promo",
    textConfig: {
      title: "SOLDES",
      subtitle: "Jusqu'à -70%",
      cta: "ACHETER",
      discount: "-70%",
    },
  },
  {
    id: "new-collection",
    name: "Nouvelle Collection",
    description: "Lancement de produit",
    preview: "🆕",
    style: "white-gradient",
    creativeType: "collection",
    textConfig: {
      title: "NOUVEAUTÉ",
      subtitle: "Collection Printemps 2025",
      cta: "Explorer",
    },
    isNew: true,
  },
  {
    id: "testimonial-trust",
    name: "Avis Client",
    description: "Preuve sociale",
    preview: "⭐",
    style: "warm-beige",
    creativeType: "testimonial",
    textConfig: {
      title: "★★★★★",
      subtitle: '"Excellent produit, je recommande!"',
      cta: "Voir les avis",
    },
  },
  {
    id: "before-after",
    name: "Avant/Après",
    description: "Comparaison transformation",
    preview: "🔄",
    style: "white-gradient",
    creativeType: "beforeafter",
    textConfig: {
      title: "Résultats Visibles",
      subtitle: "Avant → Après",
      cta: "Essayer",
    },
  },
  {
    id: "custom",
    name: "Personnalisé",
    description: "Créez votre propre template",
    preview: "🎨",
    style: "white-gradient",
    creativeType: "showcase",
    textConfig: {
      title: "",
      subtitle: "",
      cta: "",
    },
  },
];

interface AdTemplatesProps {
  selected: string | null;
  onSelect: (template: AdTemplate) => void;
}

export function AdTemplates({ selected, onSelect }: AdTemplatesProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Templates prédéfinis</span>
        <Badge variant="secondary" className="text-xs">NOUVEAU</Badge>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {AD_TEMPLATES.map((template) => {
          const isSelected = selected === template.id;
          return (
            <button
              key={template.id}
              onClick={() => onSelect(template)}
              className={cn(
                "relative p-4 rounded-xl border-2 transition-all duration-200 text-left group",
                "hover:scale-[1.02] hover:shadow-md",
                isSelected
                  ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-primary-foreground" />
                </div>
              )}
              {template.isNew && !isSelected && (
                <div className="absolute top-2 right-2">
                  <Badge className="text-[9px] px-1.5 py-0 bg-accent text-accent-foreground">
                    NOUVEAU
                  </Badge>
                </div>
              )}
              <div className="text-3xl mb-2">{template.preview}</div>
              <h4 className="font-medium text-sm">{template.name}</h4>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {template.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { AD_TEMPLATES };
